/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Flag } from 'ui/atoms/Flag/Flag';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './GovernmentMonitoring.module.scss';

type Translate = (key: string) => string;

type EventKind = 'sanctions' | 'pep' | 'ownership' | 'control' | 'threshold' | 'status';

type EventTemplate = {
  hi: boolean;
  kind: EventKind;
  headline: string;
  entity: string;
  source: string;
  flag: string;
  filingType: string;
  identifier: string;
};

type Row = EventTemplate & {
  id: number;
  born: number;
  fresh: boolean;
};

const getFilters = (t: Translate): { key: 'all' | EventKind; label: string }[] => [
  { key: 'all', label: t('All events') },
  { key: 'sanctions', label: t('Sanctions') },
  { key: 'pep', label: t('PEP') },
  { key: 'ownership', label: t('Ownership') },
  { key: 'control', label: t('Control') },
  { key: 'threshold', label: t('Threshold') },
  { key: 'status', label: t('Status') },
];

const EVENTS: EventTemplate[] = [
  {
    hi: true,
    kind: 'sanctions',
    headline: 'Ownership chain resolves to a consolidated designation',
    entity: 'SAMPLE GULF TRADING FZE',
    source: 'OFAC consolidated list',
    flag: 'ae',
    filingType: 'Designation update',
    identifier: 'AE-771402',
  },
  {
    hi: true,
    kind: 'pep',
    headline: 'Newly appointed director matched to a PEP register',
    entity: 'SAMPLE MERIDIAN CIVILS LTD',
    source: 'PEP sources',
    flag: 'gb',
    filingType: 'AP01 appointment',
    identifier: 'GB-13984412',
  },
  {
    hi: false,
    kind: 'ownership',
    headline: 'New intermediate holding company filed above the bidder',
    entity: 'SAMPLE HOLDINGS B.V.',
    source: 'KVK Handelsregister',
    flag: 'nl',
    filingType: 'Shareholding filing',
    identifier: 'NL-74118093',
  },
  {
    hi: true,
    kind: 'control',
    headline: 'Aggregated indirect stake crosses the NSIA notification threshold',
    entity: 'SAMPLE VANTOR SYSTEMS LTD',
    source: 'Companies House',
    flag: 'gb',
    filingType: 'PSC change',
    identifier: 'GB-11928374',
  },
  {
    hi: true,
    kind: 'threshold',
    headline: 'Beneficial owner aggregate crosses 50% across three vehicles',
    entity: 'SAMPLE MARINE LOGISTICS LTD',
    source: 'Multiple registers',
    flag: 'ch',
    filingType: 'Ownership recalculation',
    identifier: 'CH-CHE241886',
  },
  {
    hi: false,
    kind: 'status',
    headline: 'Confirmation statement overdue by 41 days',
    entity: 'SAMPLE PORT SERVICES',
    source: 'RCS Luxembourg',
    flag: 'lu',
    filingType: 'Annual filing',
    identifier: 'LU-B241886',
  },
  {
    hi: false,
    kind: 'sanctions',
    headline: 'Sanctions designation removed after delisting',
    entity: 'SAMPLE CAPITAL S.à r.l.',
    source: 'EU consolidated list',
    flag: 'lu',
    filingType: 'List update',
    identifier: 'LU-B198765',
  },
  {
    hi: false,
    kind: 'pep',
    headline: 'Close associate of a PEP recorded as shareholder',
    entity: 'SAMPLE NORTH HOLDINGS B.V.',
    source: 'PEP sources',
    flag: 'nl',
    filingType: 'Shareholding filing',
    identifier: 'NL-88213340',
  },
  {
    hi: true,
    kind: 'ownership',
    headline: 'Ultimate beneficial owner changed on the parent undertaking',
    entity: 'SAMPLE HOLDINGS B.V.',
    source: 'KVK Handelsregister',
    flag: 'nl',
    filingType: 'UBO filing',
    identifier: 'NL-74118093',
  },
  {
    hi: false,
    kind: 'status',
    headline: 'Registered office changed to a registered-agent address',
    entity: 'SAMPLE GROUNDWORKS LTD',
    source: 'Companies House',
    flag: 'gb',
    filingType: 'AD01 filing',
    identifier: 'GB-15532210',
  },
  {
    hi: false,
    kind: 'threshold',
    headline: 'Ownership-based threshold crossed on a subsidiary',
    entity: 'SAMPLE MARINE (IE)',
    source: 'CRO',
    flag: 'ie',
    filingType: 'Beneficial ownership filing',
    identifier: 'IE-611234',
  },
  {
    hi: true,
    kind: 'status',
    headline: 'Compulsory strike-off action started',
    entity: 'SAMPLE LOWLAND UTILITIES LTD',
    source: 'Companies House',
    flag: 'gb',
    filingType: 'Gazette notice',
    identifier: 'GB-10442219',
  },
];

const SEED_COUNT = 6;
const MAX_ROWS = 8;
const PUSH_MS = 2400;
const INITIAL_TODAY = 214;

const ago = (ms: number) => {
  const s = Math.floor(ms / 1000);
  if (s < 60) {
    return `${s}s`;
  }
  if (s < 3600) {
    return `${Math.floor(s / 60)}m`;
  }
  return `${Math.floor(s / 3600)}h`;
};

const formatClock = (date: Date) => `${date.toISOString().slice(11, 19)} UTC`;

const seedRows = (): Row[] =>
  Array.from({ length: SEED_COUNT }, (_, j) => ({
    ...EVENTS[j % EVENTS.length],
    id: j,
    born: Date.now() - j * 31000,
    fresh: false,
  }));

export const GovernmentMonitoring = () => {
  const { t } = useInt();
  const filters = getFilters(t);
  const [rows, setRows] = useState<Row[]>(seedRows);
  const [filter, setFilter] = useState<'all' | EventKind>('all');
  const [paused, setPaused] = useState(false);
  const [now, setNow] = useState<number | null>(null);
  const [today, setToday] = useState(INITIAL_TODAY);

  const liveRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(liveRef, { threshold: 0.2 });
  const pausedRef = useRef(paused);
  const idxRef = useRef(SEED_COUNT);
  const nextIdRef = useRef(SEED_COUNT);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  const push = () => {
    if (pausedRef.current) {
      return;
    }

    const template = EVENTS[idxRef.current % EVENTS.length];
    idxRef.current += 1;
    const id = nextIdRef.current;
    nextIdRef.current += 1;

    setRows((prev) => {
      const next = [{ ...template, id, born: Date.now(), fresh: true }, ...prev];
      return next.length > MAX_ROWS ? next.slice(0, MAX_ROWS) : next;
    });
    setToday((t) => t + 1);

    setTimeout(() => {
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, fresh: false } : r)));
    }, 3400);
  };

  useEffect(() => {
    if (!revealed) {
      return undefined;
    }

    setNow(Date.now());
    const clockId = setInterval(() => setNow(Date.now()), 1000);
    const pushId = setInterval(push, PUSH_MS);

    return () => {
      clearInterval(clockId);
      clearInterval(pushId);
    };
  }, [revealed]);

  const visibleRows = rows.filter((row) => filter === 'all' || row.kind === filter);

  return (
    <Section background="#F7F9FD" className={styles.root}>
      <div className={styles.head}>
        <span className={styles.badge}>
          <i />
          {t('Perpetual monitoring')}
        </span>
        <TitleBlock
          title={t('A supplier cleared at award can be sanctioned by lunchtime.')}
          description={
            <>
              <b>
                {t(
                  'Perpetual monitoring keeps every entity in your register under watch from the day the contract is signed until the day it expires.',
                )}
              </b>{' '}
              {t('Changes are surfaced when they are filed, not at the next scheduled review.')}
            </>
          }
        />
      </div>

      <div className={styles.live} ref={liveRef}>
        <div className={styles.liveHead}>
          <span className={cn(styles.dot, paused && styles.dotPaused)} />
          <span className={cn(styles.liveTag, paused && styles.liveTagPaused)}>{t('Supplier register watch')}</span>
          <span className={styles.liveMeta}>2,418 entities · 41 jurisdictions</span>
          <span className={styles.liveCount}>
            <b>{today.toLocaleString()}</b> {t('today')}
          </span>
          <span className={styles.clock}>{now ? formatClock(new Date(now)) : '--:--:-- UTC'}</span>
        </div>

        <div className={styles.controls}>
          {filters.map((f) => (
            <button
              type="button"
              className={styles.filterBtn}
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
              key={f.key}
            >
              {f.label}
            </button>
          ))}
          <button type="button" className={styles.pauseBtn} aria-pressed={paused} onClick={() => setPaused((p) => !p)}>
            <i />
            {paused ? t('Resume') : t('Pause')}
          </button>
        </div>

        <div className={styles.body}>
          {visibleRows.map((row) => (
            <div className={cn(styles.row, row.hi && styles.rowHi, row.fresh && styles.rowFresh)} key={row.id}>
              <div className={styles.rowMain}>
                <span className={styles.sev} />
                <span className={styles.kd}>
                  {filters.find((f) => f.key === row.kind)?.label}
                  {row.fresh && <i className={styles.newtag}>{t('New')}</i>}
                </span>
                <span className={styles.rowBody}>
                  <span className={styles.hl}>{row.headline}</span>
                  <span className={styles.ent}>{row.entity}</span>
                </span>
                <span className={styles.sr}>
                  <Flag code={row.flag} className={styles.srFlag} />
                  {row.source}
                </span>
                <span className={styles.filing}>
                  {row.filingType} · {row.identifier}
                </span>
                <span className={styles.ago}>{now ? ago(now - row.born) : 'now'}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.foot}>
          <span>{t('Sample stream · fictional entities')}</span>
          <span className={styles.hint}>{t('Every event carries the register that filed it')}</span>
        </div>
      </div>
    </Section>
  );
};
